import { PROTOCOL_VERSION } from '../../core/protocols';
import { createSeededRandom } from '../../core/random';
import { clampDifficulty } from '../../core/staircase';
import type { SessionMode, TrialPlan, TrialResult } from '../../core/types';
import { pingPong } from '../canvas';
import type {
  ModuleFeedback,
  ResponseOption,
  RuntimeTrainingModule,
  TrainingFrameContext,
} from '../runtime';
import { estimateFixedFormThreshold } from './fixed-threshold';
import { estimateReversalThreshold } from './reversal-threshold';
import { adaptDifficultyToPerformance } from '../adaptive-difficulty';

export interface TrackObject {
  id: number;
  startXDegrees: number;
  startYDegrees: number;
  velocityX: number;
  velocityY: number;
}

export interface PlayerTrackStimulus {
  objects: TrackObject[];
  targetIds: number[];
  cueMs: 1200;
  staticDelayMs: 800;
  trackingMs: 5000;
  speedDegreesPerSecond: number;
}

const OBJECT_COUNT = 8;
const TARGET_COUNT = 4;
const TRACKING_MS = 5000 as const;
export const PLAYER_TRACK_X_LIMIT_DEGREES = 10.5;
export const PLAYER_TRACK_Y_LIMIT_DEGREES = 6.2;
export const PLAYER_TRACK_MIN_SEPARATION_DEGREES = 1.6;
const DIRECTION_SEARCH_STEP_RADIANS = Math.PI / 36;

function bounce(value: number, lower: number, upper: number): number {
  return lower + pingPong(value - lower, upper - lower);
}

export function trackObjectPosition(
  object: TrackObject,
  elapsedMs: number,
): { xDegrees: number; yDegrees: number } {
  const seconds = Math.max(0, elapsedMs) / 1000;
  return {
    xDegrees: bounce(
      object.startXDegrees + object.velocityX * seconds,
      -PLAYER_TRACK_X_LIMIT_DEGREES,
      PLAYER_TRACK_X_LIMIT_DEGREES,
    ),
    yDegrees: bounce(
      object.startYDegrees + object.velocityY * seconds,
      -PLAYER_TRACK_Y_LIMIT_DEGREES,
      PLAYER_TRACK_Y_LIMIT_DEGREES,
    ),
  };
}

export function minimumPlayerTrackSeparation(
  stimulus: Pick<PlayerTrackStimulus, 'objects' | 'trackingMs'>,
  sampleRateHz = 120,
): number {
  let minimum = Number.POSITIVE_INFINITY;
  const samples = Math.ceil((stimulus.trackingMs / 1000) * sampleRateHz);
  for (let sample = 0; sample <= samples; sample += 1) {
    const elapsedMs = Math.min(stimulus.trackingMs, (sample / sampleRateHz) * 1000);
    const positions = stimulus.objects.map((object) => trackObjectPosition(object, elapsedMs));
    for (let left = 0; left < positions.length; left += 1) {
      for (let right = left + 1; right < positions.length; right += 1) {
        const a = positions[left];
        const b = positions[right];
        if (a && b) minimum = Math.min(minimum, Math.hypot(a.xDegrees - b.xDegrees, a.yDegrees - b.yDegrees));
      }
    }
  }
  return minimum;
}

export function minimumPlayerTrackFinalSeparation(
  stimulus: Pick<PlayerTrackStimulus, 'objects' | 'trackingMs'>,
): number {
  const positions = stimulus.objects.map((object) =>
    trackObjectPosition(object, stimulus.trackingMs),
  );
  let minimum = Number.POSITIVE_INFINITY;
  for (let left = 0; left < positions.length; left += 1) {
    for (let right = left + 1; right < positions.length; right += 1) {
      const a = positions[left];
      const b = positions[right];
      if (a && b) {
        minimum = Math.min(
          minimum,
          Math.hypot(a.xDegrees - b.xDegrees, a.yDegrees - b.yDegrees),
        );
      }
    }
  }
  return minimum;
}

function directionOffset(candidateIndex: number): number {
  if (candidateIndex === 0) return 0;
  const step = Math.ceil(candidateIndex / 2);
  return (candidateIndex % 2 === 1 ? step : -step) * DIRECTION_SEARCH_STEP_RADIANS;
}

function separatePlayerTrackFinalPositions(
  objects: readonly TrackObject[],
  speedDegreesPerSecond: number,
  trackingMs: number,
): TrackObject[] {
  const assigned = new Array<TrackObject>(objects.length);
  const finalPositions = new Array<{ xDegrees: number; yDegrees: number }>(objects.length);
  const candidateCount = Math.round((Math.PI * 2) / DIRECTION_SEARCH_STEP_RADIANS) + 1;

  const assignObject = (objectIndex: number): boolean => {
    if (objectIndex === objects.length) return true;
    const object = objects[objectIndex];
    if (!object) return false;
    const originalDirection = Math.atan2(object.velocityY, object.velocityX);

    for (let candidateIndex = 0; candidateIndex < candidateCount; candidateIndex += 1) {
      const direction = originalDirection + directionOffset(candidateIndex);
      const candidate: TrackObject = candidateIndex === 0
        ? object
        : {
            ...object,
            velocityX: Math.cos(direction) * speedDegreesPerSecond,
            velocityY: Math.sin(direction) * speedDegreesPerSecond,
          };
      const finalPosition = trackObjectPosition(candidate, trackingMs);
      const separated = finalPositions.slice(0, objectIndex).every((other) =>
        other !== undefined
        && Math.hypot(
          finalPosition.xDegrees - other.xDegrees,
          finalPosition.yDegrees - other.yDegrees,
        ) >= PLAYER_TRACK_MIN_SEPARATION_DEGREES,
      );
      if (!separated) continue;

      assigned[objectIndex] = candidate;
      finalPositions[objectIndex] = finalPosition;
      if (assignObject(objectIndex + 1)) return true;
    }
    return false;
  };

  if (!assignObject(0)) {
    throw new Error('Unable to create separated Player Track response positions');
  }
  return assigned;
}

export function validatePlayerTrackTrajectories(stimulus: PlayerTrackStimulus): boolean {
  if (stimulus.objects.length !== OBJECT_COUNT || stimulus.targetIds.length !== TARGET_COUNT) return false;
  const objectIds = new Set(stimulus.objects.map(({ id }) => id));
  if (objectIds.size !== OBJECT_COUNT || stimulus.targetIds.some((id) => !objectIds.has(id))) return false;
  // Moving objects may visually occlude. The initial cue and stopped response
  // array must remain separated so each marker can be identified and selected.
  for (let left = 0; left < stimulus.objects.length; left += 1) {
    for (let right = left + 1; right < stimulus.objects.length; right += 1) {
      const a = stimulus.objects[left];
      const b = stimulus.objects[right];
      if (a && b && Math.hypot(a.startXDegrees - b.startXDegrees, a.startYDegrees - b.startYDegrees) < PLAYER_TRACK_MIN_SEPARATION_DEGREES) return false;
    }
  }
  return stimulus.objects.every((object) => [0, stimulus.trackingMs].every((elapsedMs) => {
    const position = trackObjectPosition(object, elapsedMs);
    return Math.abs(position.xDegrees) <= PLAYER_TRACK_X_LIMIT_DEGREES
      && Math.abs(position.yDegrees) <= PLAYER_TRACK_Y_LIMIT_DEGREES;
  })) && minimumPlayerTrackFinalSeparation(stimulus) >= PLAYER_TRACK_MIN_SEPARATION_DEGREES;
}

export function difficultyToTrackSpeed(difficulty: number): number {
  return Number((7.4 + (clampDifficulty(difficulty, 1, 5) - 1) * 1.25).toFixed(2));
}

export function createPlayerTrackTargetIds(seed: number): number[] {
  return createSeededRandom(seed ^ 0x51a9_03d7)
    .shuffle(Array.from({ length: OBJECT_COUNT }, (_, index) => index + 1))
    .slice(0, TARGET_COUNT)
    .sort((left, right) => left - right);
}

export function createPlayerTrackStimulus(seed: number, difficulty: number): PlayerTrackStimulus {
  const random = createSeededRandom(seed);
  const speedDegreesPerSecond = difficultyToTrackSpeed(difficulty);
  const grid = random.shuffle([
    [-8.2, -3.8], [-2.8, -3.8], [2.8, -3.8], [8.2, -3.8],
    [-8.2, 3.8], [-2.8, 3.8], [2.8, 3.8], [8.2, 3.8],
  ] as const);
  const initialObjects = grid.map((point, index) => {
    const angle = random.range(0, Math.PI * 2);
    return {
      id: index + 1,
      startXDegrees: point[0] + random.range(-0.2, 0.2),
      startYDegrees: point[1] + random.range(-0.2, 0.2),
      velocityX: Math.cos(angle) * speedDegreesPerSecond,
      velocityY: Math.sin(angle) * speedDegreesPerSecond,
    };
  });
  const objects = separatePlayerTrackFinalPositions(
    initialObjects,
    speedDegreesPerSecond,
    TRACKING_MS,
  );
  const stimulus: PlayerTrackStimulus = {
    objects,
    targetIds: createPlayerTrackTargetIds(seed),
    cueMs: 1200,
    staticDelayMs: 800,
    trackingMs: TRACKING_MS,
    speedDegreesPerSecond,
  };
  if (!validatePlayerTrackTrajectories(stimulus)) throw new Error(`Invalid MOT stimulus for seed ${seed}`);
  return stimulus;
}

export function playerTrackMarkerRadiusPixels(pixelsPerDegree: number): number {
  return Math.max(6, pixelsPerDegree * 0.65);
}

function renderPlayerTrack(
  context: CanvasRenderingContext2D,
  frame: TrainingFrameContext,
  trial: TrialPlan<PlayerTrackStimulus>,
): void {
  context.save();
  context.setTransform(frame.devicePixelRatio, 0, 0, frame.devicePixelRatio, 0, 0);
  context.fillStyle = '#050606';
  context.fillRect(0, 0, frame.width, frame.height);
  const centerX = frame.width / 2;
  const centerY = frame.height / 2;
  const stimulus = trial.stimulus;
  const radius = playerTrackMarkerRadiusPixels(frame.pixelsPerDegree);
  const motionStart = stimulus.cueMs + stimulus.staticDelayMs;
  const responsePhase = frame.elapsed >= trial.timings.stimulusMs;
  const moving = frame.elapsed >= motionStart && !responsePhase;
  const motionElapsed = moving
    ? Math.min(stimulus.trackingMs, frame.elapsed - motionStart)
    : responsePhase ? stimulus.trackingMs : 0;

  context.strokeStyle = '#59605d';
  context.lineWidth = 1;
  context.strokeRect(
    centerX - PLAYER_TRACK_X_LIMIT_DEGREES * frame.pixelsPerDegree - radius,
    centerY - PLAYER_TRACK_Y_LIMIT_DEGREES * frame.pixelsPerDegree - radius,
    PLAYER_TRACK_X_LIMIT_DEGREES * 2 * frame.pixelsPerDegree + radius * 2,
    PLAYER_TRACK_Y_LIMIT_DEGREES * 2 * frame.pixelsPerDegree + radius * 2,
  );

  for (const object of stimulus.objects) {
    const target = stimulus.targetIds.includes(object.id);
    if (frame.elapsed < stimulus.cueMs && !target) continue;
    const position = trackObjectPosition(object, motionElapsed);
    const x = centerX + position.xDegrees * frame.pixelsPerDegree;
    const y = centerY + position.yDegrees * frame.pixelsPerDegree;
    const cuedTarget = frame.elapsed < stimulus.cueMs && target;
    const fadeProgress = frame.elapsed < stimulus.cueMs
      ? 1
      : Math.min(1, Math.max(0, (frame.elapsed - stimulus.cueMs) / stimulus.staticDelayMs));
    context.globalAlpha = target ? 1 : fadeProgress;
    context.fillStyle = '#f3f4f1';
    context.strokeStyle = '#050606';
    context.lineWidth = 2;
    context.beginPath();
    context.arc(x, y, radius, 0, Math.PI * 2);
    context.fill();
    context.stroke();
    if (cuedTarget) {
      context.strokeStyle = '#d95a4e';
      context.lineWidth = 4;
      context.beginPath();
      context.arc(x, y, radius + 8, 0, Math.PI * 2);
      context.stroke();
    }
    if (responsePhase) {
      const selected = frame.selectedResponses?.includes(String(object.id)) ?? false;
      if (selected) {
        context.strokeStyle = '#9ed5c5';
        context.lineWidth = 5;
        context.beginPath();
        context.arc(x, y, radius + 8, 0, Math.PI * 2);
        context.stroke();
      }
      context.fillStyle = '#111513';
      context.font = `800 ${Math.max(14, radius)}px ui-monospace, monospace`;
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      context.fillText(String(object.id), x, y + 1);
    }
    context.globalAlpha = 1;
  }
  context.restore();
}

export function getPlayerTrackCanvasResponse(
  trial: TrialPlan<PlayerTrackStimulus>,
  frame: TrainingFrameContext,
  xCss: number,
  yCss: number,
): string | undefined {
  const centerX = frame.width / 2;
  const centerY = frame.height / 2;
  const hitRadius = Math.max(24, playerTrackMarkerRadiusPixels(frame.pixelsPerDegree) + 14);
  let nearest: { id: number; distance: number } | undefined;
  for (const object of trial.stimulus.objects) {
    const position = trackObjectPosition(object, trial.stimulus.trackingMs);
    const x = centerX + position.xDegrees * frame.pixelsPerDegree;
    const y = centerY + position.yDegrees * frame.pixelsPerDegree;
    const distance = Math.hypot(xCss - x, yCss - y);
    if (distance <= hitRadius && (!nearest || distance < nearest.distance)) {
      nearest = { id: object.id, distance };
    }
  }
  return nearest ? String(nearest.id) : undefined;
}

export function adaptPlayerTrackDifficulty(current: number, recentResults: TrialResult[]): number {
  return adaptDifficultyToPerformance(current, recentResults);
}

export const playerTrackModule: RuntimeTrainingModule<PlayerTrackStimulus> = {
  id: 'player-track',
  label: '動く8個の中から4個を追い続ける',
  shortLabel: '複数対象追跡',
  protocolVersion: PROTOCOL_VERSION,
  evidenceLabel: '製品化された複数追跡を参考にした構成',
  instructions: [
    '最初に表示される4個の点を覚えます。その後、同じ見た目の点が4個加わります。',
    '8個が5秒間動く間、最初の4個を同時に追います。',
    '停止後、追っていた点を画面上で4個選び、「選択を確定」を押します。移動中は点同士が重なることがあります。',
  ],
  responseKind: 'multi-select',
  selectionCount: TARGET_COUNT,
  createTrial(seed: number, difficulty: number, mode: SessionMode): TrialPlan<PlayerTrackStimulus> {
    const boundedDifficulty = clampDifficulty(difficulty, 1, 5);
    const stimulus = createPlayerTrackStimulus(seed, boundedDifficulty);
    return {
      id: `player-track-${mode}-${seed}`,
      moduleId: 'player-track',
      protocolVersion: PROTOCOL_VERSION,
      seed,
      stimulusId: `mot-${stimulus.targetIds.join('')}-${stimulus.speedDegreesPerSecond}-${seed}`,
      stimulus,
      correctResponse: stimulus.targetIds.join(','),
      timings: {
        fixationMs: 500,
        stimulusMs: stimulus.cueMs + stimulus.staticDelayMs + stimulus.trackingMs,
        responseWindowMs: 7000,
      },
      difficulty: boundedDifficulty,
    };
  },
  render: renderPlayerTrack,
  isResponseAllowed(response: string): boolean {
    const values = response.split(',');
    return values.length === TARGET_COUNT
      && new Set(values).size === TARGET_COUNT
      && values.every((value) => /^[1-8]$/.test(value));
  },
  adaptDifficulty: adaptPlayerTrackDifficulty,
  getResponseOptions(): readonly ResponseOption[] {
    return Array.from({ length: OBJECT_COUNT }, (_, index) => {
      const value = String(index + 1);
      return { value, keys: [value], label: `点 ${value}`, visual: 'location' };
    });
  },
  getCanvasResponse(trial, frame, xCss, yCss): string | undefined {
    return getPlayerTrackCanvasResponse(trial, frame, xCss, yCss);
  },
  getFeedback(
    trial: TrialPlan<PlayerTrackStimulus>,
    response: string | undefined,
    correct: boolean,
  ): ModuleFeedback {
    return {
      title: correct ? '4個すべて一致しました' : '追跡対象を確認してください',
      detail: response ? `選択：${response.replaceAll(',', ' / ')}` : '制限時間内に4個を確定できませんでした。',
      correctLabel: trial.stimulus.targetIds.join(' / '),
    };
  },
  getResultMetadata(trial: TrialPlan<PlayerTrackStimulus>, response: string | undefined) {
    const selected = new Set(response?.split(',').filter((value) => /^[1-8]$/.test(value)) ?? []);
    const correctSelections = trial.stimulus.targetIds.filter((id) => selected.has(String(id))).length;
    return {
      speedDegreesPerSecond: trial.stimulus.speedDegreesPerSecond,
      targetIds: trial.stimulus.targetIds.join(','),
      targetCount: TARGET_COUNT,
      selectedCount: selected.size,
      correctSelections,
      selectionAccuracy: correctSelections / TARGET_COUNT,
      incorrectSelections: Math.max(0, selected.size - correctSelections),
      minimumSeparationDegrees: minimumPlayerTrackSeparation(trial.stimulus),
      cueMs: trial.stimulus.cueMs,
      staticDelayMs: trial.stimulus.staticDelayMs,
      trackingMs: trial.stimulus.trackingMs,
      occlusionAllowed: true,
      referenceProduct: 'NeuroTracker / BrainHQ Target Tracker',
    };
  },
  getThreshold(results: readonly TrialResult[]) {
    const isFixedAssessment = results.some((result) => result.moduleId === 'player-track' && result.metadata.assessment === true);
    const fixed = estimateFixedFormThreshold(results, {
      moduleId: 'player-track',
      metadataKey: 'speedDegreesPerSecond',
      performanceKey: 'selectionAccuracy',
      direction: 'decreasing',
    });
    if (fixed !== undefined) return { value: fixed, unit: 'speed' };
    if (isFixedAssessment) return undefined;
    const value = estimateReversalThreshold(results, {
      moduleId: 'player-track',
      metadataKey: 'speedDegreesPerSecond',
      lastReversals: 6,
    });
    return value === undefined ? undefined : { value, unit: 'speed' };
  },
};
